import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { AuthStorageService, BaseSiteService, OccEndpointsService, UserIdService } from '@spartacus/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, pluck, publishReplay, refCount, switchMap, take } from 'rxjs/operators';

export interface RequestAccountPayload {
  uid?: string;
  businessType?: string;
  countryIsoCode?: string;
  regionIsoCode?: string;
  language?: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  registrationNumber?: string;
  line1?: string;
  line2?: string;
  town?: string;
  postalCode?: string;
  prefix?: string;
  cellphone?: string;
  companyCode?: boolean;
  representativeName?: string;
  corporateName?: string;
  shopURL?: string;
  yearsInBusiness?: number;
  mainCustomer?: string;
  fax?: string;
}

export interface GetCountryUserPayload {
  fields?: string;
  accountType?: string | string[];
}

export interface GetLanguageUserPayload {
  fields?: string;
  businessType?: string | string[];
  countryIsoCode?: string | string[];
}

export interface GetStateUserPayload {
  fields?: string;
  countryIsoCode?: string | string[];
}

export interface GetCountryInfoPayload {
  fields?: string;
  countryIsoCode?: string | string[];
}

@Injectable({
  providedIn: 'root',
})
export class RequestAccountService {
  tokenId: any;
  paymentAddress: any = {};
  country: any = [];
  region: any = [];

  REQUEST_ACCOUNT_FEATURES_CONFIG = '../../../public/features/REQUEST_ACCOUNT_FEATURES.json';

  cacheWindowTime = 3600000;
  private cachedRequestAccounEnabled$: any = {};
  private cachedRequestAccountFeaturesConfig$: any;

  constructor(
    protected http: HttpClient,
    protected auth: UserIdService,
    protected authStorageService: AuthStorageService,
    protected baseService: BaseSiteService,
    private meta: Meta,
    private occEndpointsService: OccEndpointsService
  ) {}

  postRequestAccount(payload: RequestAccountPayload): Observable<any> {
    this.authStorageService
      .getToken()
      .pipe(map((data) => data.access_token))
      .subscribe((results: any) => {
        this.tokenId = results;
      });

    this.country = {
      isocode: payload.countryIsoCode,
    };

    if (payload.regionIsoCode !== '' || payload.regionIsoCode !== null) {
      this.region = {
        isocode: payload.regionIsoCode,
      };
    }

    this.paymentAddress = {
      line1: payload.line1,
      line2: payload.line2,
      email: payload.email,
      town: payload.town,
      postalCode: payload.postalCode,
      prefix: payload.prefix,
      cellphone: payload.cellphone,
      fax: payload.fax,
      country: this.country,
      region: this.region,
    };

    if (this.region.isocode === null || typeof payload.regionIsoCode === 'undefined') {
      this.paymentAddress = Object.keys(this.paymentAddress).reduce((object: any, key) => {
        if (key !== 'region') {
          object[key] = this.paymentAddress[key];
        }
        return object;
      }, {});
    }

    if (payload.line2 === null || typeof payload.line2 === 'undefined') {
      this.paymentAddress = Object.keys(this.paymentAddress).reduce((object: any, key) => {
        if (key !== 'line2') {
          object[key] = this.paymentAddress[key];
        }
        return object;
      }, {});
    }

    if (payload.fax === null || typeof payload.fax === 'undefined') {
      this.paymentAddress = Object.keys(this.paymentAddress).reduce((object: any, key) => {
        if (key !== 'fax') {
          object[key] = this.paymentAddress[key];
        }
        return object;
      }, {});
    }

    const body = {
      uid: payload.uid,
      businessType: payload.businessType,
      language: payload.language,
      firstName: payload.firstName,
      lastName: payload.lastName,
      registrationNumber: payload.registrationNumber,
      companyName: payload.companyName,
      companyCode: payload.companyCode,
      representativeName: payload.representativeName,
      corporateName: payload.corporateName,
      shopURL: payload.shopURL,
      yearsInBusiness: payload.yearsInBusiness,
      mainCustomer: payload.mainCustomer,
      defaultPaymentAddress: this.paymentAddress,
    };

    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http
      .post(this.occEndpointsService.buildUrl(`/users`), JSON.stringify(body), {
        headers,
      })
      .pipe(map((data) => data));
  }

  getCountriesForRequestAccount(payload: GetCountryUserPayload): Observable<any> {
    this.authStorageService
      .getToken()
      .pipe(map((data) => data.access_token))
      .subscribe((results) => {
        this.tokenId = results;
      });

    const params = new HttpParams().set('fields', payload.fields);

    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    return this.http
      .get(this.occEndpointsService.buildUrl(`dynamicForms/${payload.accountType}/countries`), {
        params,
        headers,
      })
      .pipe(
        switchMap((countriesData) =>
          forkJoin([of(countriesData), this.getRequestAccountFeaturesConfig$().pipe(pluck('country'))])
        ),
        map(([countriesData, requestAccountFeaturesConfigData]: [any, any]) => {
          return {
            items: countriesData.items.filter(
              (country: any) => country['extraProperties']['entry'][3].value === 'true'
            ),
          };
        })
      );
  }

  getLanguageForRequestAccount(payload: GetLanguageUserPayload): Observable<any> {
    this.authStorageService
      .getToken()
      .pipe(map((data) => data.access_token))
      .subscribe((results) => {
        this.tokenId = results;
      });

    const params = new HttpParams()
      .set('fields', payload.fields)
      .set('businessType', payload.businessType as string)
      .set('countryIsoCode', payload.countryIsoCode as string);

    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    return this.http
      .get(this.occEndpointsService.buildUrl(`dynamicForms/languages`), {
        params,
        headers,
      })
      .pipe(map((data) => data));
  }

  getStateForRequestAccount(payload: GetStateUserPayload): Observable<any> {
    this.authStorageService
      .getToken()
      .pipe(map((data) => data.access_token))
      .subscribe((results) => {
        this.tokenId = results;
      });

    const params = new HttpParams().set('fields', payload.fields);

    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    return this.http
      .get(this.occEndpointsService.buildUrl(`dynamicForms/${payload.countryIsoCode}/regions`), {
        params,
        headers,
      })
      .pipe(map((data) => data));
  }

  getCountryInfo(payload: GetCountryInfoPayload): Observable<any> {
    this.authStorageService
      .getToken()
      .pipe(map((data) => data.access_token))
      .subscribe((results: any) => {
        this.tokenId = results;
      });

    const params = new HttpParams().set('fields', payload.fields);

    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    return this.http
      .get(this.occEndpointsService.buildUrl(`dynamicForms/${payload.countryIsoCode}/country`), {
        params,
        headers,
      })
      .pipe(map((data) => data));
  }

  getRequestAccountFeaturesConfig$(): Observable<any> {
    if (!this.cachedRequestAccountFeaturesConfig$) {
      this.cachedRequestAccountFeaturesConfig$ = this.http
        .get(this.REQUEST_ACCOUNT_FEATURES_CONFIG)
        .pipe(publishReplay(1, this.cacheWindowTime), refCount(), take(1), catchError(this.errorHandlr));
    }
    return this.cachedRequestAccountFeaturesConfig$;
  }

  isRequestAccountEnabled(activeCountry: string): Observable<boolean> {
    if (!this.cachedRequestAccounEnabled$[activeCountry]) {
      this.cachedRequestAccounEnabled$[activeCountry] = this.getRequestAccountFeaturesConfig$().pipe(
        publishReplay(1, this.cacheWindowTime),
        refCount(),
        take(1),
        pluck('country'),
        map((data: any) => (data[activeCountry] !== undefined ? data[activeCountry] : data['default'])),
        catchError(this.errorHandlr)
      );
    }
    return this.cachedRequestAccounEnabled$[activeCountry];
  }

  errorHandlr(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(errorMessage);
  }
}
